import { OperationType } from "@/types/dataset";

// Error message mappings for failed operations
const ERROR_MESSAGE_MAP: {
  keyword: string;
  title: string;
  description: string;
}[] = [
  {
    keyword: "insufficient storage capacity",
    title: "Upload Failed",
    description:
      "File size exceeds your available quota. Please delete existing datasets or upgrade your plan"
  },
  {
    keyword: "invalid JSON format",
    title: "Invalid File Format",
    description:
      "Invalid JSON format. Please check for missing commas or brackets."
  },
  {
    keyword: "GeoJSON must be a FeatureCollection",
    title: "Invalid Structure",
    description: "GeoJSON must be a FeatureCollection."
  },
  {
    keyword: "type can't be null or empty",
    title: "Parsing Failed",
    description: "Field type cannot be null or empty."
  },
  {
    keyword: "missing required field: type",
    title: "Invalid Structure",
    description: "Feature is missing required field: type."
  },
  {
    keyword: "missing required field: properties",
    title: "Invalid Structure",
    description: "Feature is missing required field: properties."
  },
  {
    keyword: "missing required field: geometry",
    title: "Invalid Structure",
    description: "Feature is missing required field: geometry."
  },
  {
    keyword: "missing required <kml>",
    title: "Invalid KML File",
    description:
      "The KML file is missing the required <kml> root element and cannot be processed."
  },
  {
    keyword: "does not contain any layers",
    title: "No Data Found",
    description:
      "The KML file does not contain any valid placemarks and cannot be processed."
  },
  {
    keyword: "not contain a supported geometry",
    title: "Missing Geometry",
    description: "One or more Placemarks do not contain a supported geometry."
  },
  {
    keyword: "Geometry cannot be empty",
    title: "Invalid Geometry",
    description: "Geometry coordinates cannot be empty."
  },
  {
    keyword: "unsupported coordinate system",
    title: "Unsupported coordinate system.",
    description:
      "The file must use the WGS84 (EPSG:4326) coordinate system. Please convert the file and upload it again."
  },
  {
    keyword: "unsupported geometry",
    title: "Unsupported Geometry Type",
    description:
      "This file contains unsupported geometry types. Only Point, LineString, and Polygon are supported."
  },
  {
    keyword: "GeometryCollection",
    title: "Unsupported KML Element",
    description: "GeometryCollection is not supported and cannot be uploaded."
  },
  {
    keyword: "Data name",
    title: "Invalid Attribute Data",
    description:
      "Attribute definitions must include a valid Data name. Please check your ExtendedData structure."
  },
  {
    keyword: "not supported by the system",
    title: "Unsupported KML Content",
    description:
      "This KML file contains elements that are not supported by the system."
  }
];

// Default error notification for unmatched errors
const DEFAULT_ERROR = {
  title: "Something Went Wrong",
  description:
    "Sorry, we’re experiencing an issue while processing your request. Please try again."
};

// Import operation fallback error
const IMPORT_OPERATION_ERROR = {
  title: "Data Format Mismatch",
  description:
    "Please ensure your file uses the same structure as the existing dataset"
};

export const getErrorNotification = (
  errorMessage: string | undefined,
  operationType: OperationType
): { title: string; description: string } => {
  // Match against known error message keywords
  const matched = ERROR_MESSAGE_MAP.find(({ keyword }) =>
    errorMessage?.includes(keyword)
  );

  if (matched) {
    return { title: matched.title, description: matched.description };
  }

  // Fallback for import operations
  if (
    operationType === OperationType.IMPORT_ADD ||
    operationType === OperationType.IMPORT_REPLACE
  ) {
    return IMPORT_OPERATION_ERROR;
  }

  // Default fallback
  return DEFAULT_ERROR;
};
